import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import queryString from 'query-string';
import {
  ButtonPrimary,
  ButtonPseudo,
  Checkbox,
  InputText,
  PermissionDenied,
  PermissionEnum,
  Popup,
  ResponseError,
  Select,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  ajvResolver,
  responseErrorsResolver,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  CategoryLevelEnum,
  ClientRangeFormValues,
  RiskScoreClientTypeEnum,
  SettingClientRangeType,
} from '../../types';
import {
  useCreateIndividualVolumesMutation,
  useUpdateIndividualVolumesMutation,
} from '../../indiviual-clients-risk/store/services';
import {
  useCreateCorporateVolumesMutation,
  useUpdateCorporateVolumesMutation,
} from '../../corporate-clients-risk/store/services';
import { VolumesFormTypes } from './types';

import ROUTES from '../../../routes';

import cn from '../partial.module.css';

const getRiskScoreName = (name: string) => {
  let riskScoreName;

  switch (name) {
    case CategoryLevelEnum.LOW:
      riskScoreName = 'Low';
      break;
    case CategoryLevelEnum.MEDIUM:
      riskScoreName = 'Medium';
      break;
    case CategoryLevelEnum.HIGH:
      riskScoreName = 'High';
      break;
    default:
      riskScoreName = name;
      break;
  }

  return riskScoreName;
};

const getTypeName = (type: SettingClientRangeType) => {
  let typeName;

  switch (type) {
    case SettingClientRangeType.MONTHLY_TURNOVER:
      typeName = 'Turnover';
      break;
    case SettingClientRangeType.MONTHLY_NUMBER_OF_INCOMING_TRANSACTIONS:
      typeName = 'N. of incoming transactions';
      break;
    case SettingClientRangeType.MONTHLY_NUMBER_OF_OUTGOING_TRANSACTIONS:
      typeName = 'N. of outgoing transactions';
      break;
    case SettingClientRangeType.MONTHLY_VALUE_OF_INCOMING_TRANSACTIONS:
      typeName = 'Value of incoming transactions';
      break;
    case SettingClientRangeType.MONTHLY_VALUE_OF_OUTGOING_TRANSACTIONS:
      typeName = 'Value of outgoing transactions';
      break;
    case SettingClientRangeType.MONTHLY_MAX_VALUE_OF_INCOMING_PAYMENT:
      typeName = 'Max. value of incoming payment';
      break;
    case SettingClientRangeType.MONTHLY_MAX_VALUE_OF_OUTGOING_PAYMENT:
      typeName = 'Max. value of outgoing payment';
      break;
    default:
      typeName = type;
      break;
  }

  return typeName;
};

export const schema = {
  type: 'object',
  properties: {
    riskScore: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Risk Score is required.',
      },
    },
    from: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'From is required.',
      },
    },
  },
  required: ['riskScore', 'from'],
};

let timeout: NodeJS.Timeout;

const VolumesForm = ({
  isLoading,
  clientType,
  panelIsOpen,
  setPanelIsOpen,
  formValues,
  errorList,
}: VolumesFormTypes) => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');

  const {
    amlDictionaries: { activePermissions, riskScoreRanges },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    setError,
    clearErrors,
  } = useForm<ClientRangeFormValues>({
    resolver: ajvResolver(schema),
  });

  const { itemId } = useParams();

  const [createIndividualVolumes, { isLoading: requestInProgress, isSuccess }] =
    useCreateIndividualVolumesMutation();
  const [
    createCorporateVolumes,
    { isLoading: corporateRequestInProgress, isSuccess: corporateIsSuccess },
  ] = useCreateCorporateVolumesMutation();
  const [updateIndividualVolume, { isLoading: updateInProgress }] =
    useUpdateIndividualVolumesMutation();
  const [updateCorporateVolume, { isLoading: updateCorporateInProgress }] =
    useUpdateCorporateVolumesMutation();

  const { search } = useLocation();
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setGlobalError('');

    setTimeout(() => {
      navigate(
        clientType === RiskScoreClientTypeEnum.INDIVIDUAL
          ? ROUTES.INDIVIDUAL_CLIENTS_RISK_VOLUMES
          : ROUTES.CORPORATE_CLIENTS_RISK_VOLUMES,
      );
    }, 100);
  }, [clientType]);

  const onFormSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    let requestAvailable = true;

    if (
      Number.isNaN(Number(values.from)) ||
      Number.isNaN(Number(values.riskScore))
    ) {
      requestAvailable = false;

      if (Number.isNaN(Number(values.from))) {
        setError('from', {
          type: 'custom',
          message: 'From must be an integer number',
        });
      }

      if (Number.isNaN(Number(values.riskScore))) {
        setError('riskScore', {
          type: 'custom',
          message: 'Risk Score must be an integer number',
        });
      }
    }

    if (requestAvailable) {
      if (!itemId) {
        const body: ClientRangeFormValues = {
          type: queryString.parse(search)?.type as string,
          from: Number(values.from),
          riskScore: Number(values.riskScore),
          clientType: clientType as RiskScoreClientTypeEnum,
          active: true,
        };

        if (clientType === RiskScoreClientTypeEnum.INDIVIDUAL) {
          const response = (await createIndividualVolumes(body)) as {
            error: ResponseError;
          };

          if (response.error) {
            responseErrorsResolver({
              errors: response.error,
              setError,
              setGlobalError,
            });
          }
        } else if (clientType === RiskScoreClientTypeEnum.CORPORATE) {
          const response = (await createCorporateVolumes(body)) as {
            error: ResponseError;
          };

          if (response.error) {
            responseErrorsResolver({
              errors: response.error,
              setError,
              setGlobalError,
            });
          }
        }
      } else {
        const body: ClientRangeFormValues = {
          type: values.type,
          from: Number(values.from),
          riskScore: Number(values.riskScore),
          clientType: clientType as RiskScoreClientTypeEnum,
          active: values.active,
        };

        if (clientType === RiskScoreClientTypeEnum.INDIVIDUAL) {
          const response = (await updateIndividualVolume({
            id: itemId,
            body,
          })) as {
            error: ResponseError;
          };

          if (response.error) {
            responseErrorsResolver({
              errors: response.error,
              setError,
              setGlobalError,
            });
          }
        } else if (clientType === RiskScoreClientTypeEnum.CORPORATE) {
          const response = (await updateCorporateVolume({
            id: itemId,
            body,
          })) as {
            error: ResponseError;
          };

          if (response.error) {
            responseErrorsResolver({
              errors: response.error,
              setError,
              setGlobalError,
            });
          }
        }
      }
    }
  });

  useEffect(() => {
    if (formValues) {
      const riskCategory = riskScoreRanges?.find(
        (riskScore) =>
          riskScore.from <= (formValues.riskScore as number) &&
          riskScore.to >= (formValues.riskScore as number),
      );

      reset({
        ...formValues,
        from: formValues.from.toString(),
        riskScore: formValues.riskScore.toString(),
        riskCategory: riskCategory?.name || '',
      });
    }
  }, [reset, formValues]);

  useEffect(() => {
    if (isSuccess || corporateIsSuccess) {
      handleClose();
    }
  }, [isSuccess, corporateIsSuccess]);

  const permissionDenied = !activePermissions?.includes(
    PermissionEnum.SETTINGS_TRANSACTIONS_VOLUMES_CREATE_ACTION,
  );

  const isEditable = itemId
    ? activePermissions?.includes(
        PermissionEnum.SETTINGS_TRANSACTIONS_VOLUMES_EDIT_ACTION,
      )
    : activePermissions?.includes(
        PermissionEnum.SETTINGS_TRANSACTIONS_VOLUMES_CREATE_ACTION,
      );

  let content = (
    <div className={cn.volumesForm}>
      {itemId && (
        <div className={cn.volumesFormRow}>
          <Controller
            control={control}
            name="active"
            render={({ field: { onChange, value, name } }) => (
              <Checkbox
                name={name}
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                title="Active"
                type="checkbox"
              />
            )}
          />
        </div>
      )}
      <div className={cn.volumesFormRow}>
        <Controller
          control={control}
          name="from"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <InputText
              name={name}
              placeholder="Enter From"
              onChange={(e) => {
                onChange(e);
                if (errors?.form?.message) {
                  clearErrors(['from']);
                }
              }}
              value={value}
              title="From"
              error={errors?.from?.message}
              className={cn.volumesInput}
              labelClassName={cn.volumesInputLabel}
              disabled={!isEditable}
            />
          )}
        />
      </div>
      <div className={cn.volumesFormRow}>
        <Controller
          control={control}
          name="riskScore"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <InputText
              name={name}
              placeholder="Enter Risk Score"
              onChange={(e) => {
                clearTimeout(timeout);

                onChange(e);

                if (errors?.riskScore?.message) {
                  clearErrors(['riskScore']);
                }

                timeout = setTimeout(() => {
                  const currentRange = riskScoreRanges?.find(
                    (range) =>
                      range.from <= Number(e.target.value) &&
                      Number(e.target.value) <= range.to,
                  );

                  if (currentRange) {
                    setValue('riskCategory', currentRange.name);
                  }
                }, 300);
              }}
              value={value}
              title="Risk Score"
              error={errors?.riskScore?.message}
              className={cn.volumesInput}
              labelClassName={cn.volumesInputLabel}
              disabled={!isEditable}
            />
          )}
        />
      </div>
      <div className={cn.volumesFormRow}>
        <Controller
          control={control}
          name="riskCategory"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Select
              name="riskCategory"
              placeholder="Select Risk category"
              onChange={(e) => {
                onChange(e);

                if (errors?.riskScore?.message) {
                  clearErrors(['riskScore']);
                }

                const currentCategory = riskScoreRanges?.find(
                  (range) => range.name === e.target.value,
                );

                if (currentCategory) {
                  setValue('riskScore', currentCategory.from.toString());
                }
              }}
              value={value}
              selectWrapperClassName={`${cn.selectRiskCategory} ${
                errors?.riskScore?.message ? cn.selectError : ''
              }`}
              list={riskScoreRanges?.map((range) => ({
                name: getRiskScoreName(range.name),
                value: range.name,
              }))}
              disabled={!isEditable}
              fullWidth
            />
          )}
        />
      </div>
    </div>
  );

  if (isLoading) {
    content = <SidePanelLoader />;
  } else if (!itemId && permissionDenied) {
    content = <PermissionDenied />;
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={`${itemId ? 'Edit' : 'Add'} ${
          getTypeName(
            itemId && formValues
              ? (formValues?.type as SettingClientRangeType)
              : (queryString.parse(search)?.type as SettingClientRangeType),
          ) || ''
        }`}
        sidepanelExpandedKey="volumes-panel"
        errorSidepanel={globalError || errorList}
        footer={
          <>
            {isEditable && (
              <ButtonPrimary
                text={
                  requestInProgress ||
                  corporateRequestInProgress ||
                  updateInProgress ||
                  updateCorporateInProgress
                    ? 'Loading...'
                    : 'Save'
                }
                onClick={onFormSubmit}
                disabled={
                  requestInProgress ||
                  corporateRequestInProgress ||
                  updateInProgress ||
                  updateCorporateInProgress
                }
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default VolumesForm;
